/**
 * Hoje — a tela inicial do aluno.
 *
 * No Marco 0 mostra o esqueleto e o estado de saúde da API: é o que prova o
 * caminho completo navegador → API → banco, com token verificado e RLS ativa.
 * O treino do dia chega no Marco 3.
 */

import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { api } from '@/lib/apiClient';
import { formatDate, formatWeekday, weekdayLabels, startOfWeek } from '@/lib/datetime';
import { useSession } from '@/features/auth/useSession';

interface ReadyResponse {
  status: 'ok' | 'degraded';
  checks: Record<string, string>;
}

export default function TodayPage() {
  const { t, i18n } = useTranslation(['common']);
  const { session } = useSession();
  const today = new Date();

  const { data, isPending } = useQuery({
    queryKey: ['health'],
    queryFn: () => api.get<ReadyResponse>('/health/ready'),
    retry: 1,
    staleTime: 30_000,
  });

  const name = (session?.user.user_metadata?.display_name as string | undefined) ?? '';
  const weekStart = startOfWeek(today, i18n.language);

  return (
    <div className="flex flex-col gap-5 px-5 py-6">
      <header>
        <p className="text-sm text-(--color-sand-700)">
          {formatWeekday(today, i18n.language)}, {formatDate(today, i18n.language)}
        </p>
        <h1 className="mt-1 text-3xl">{t('common:today.greeting', { name })}</h1>
      </header>

      <section className="mh-card" aria-labelledby="week-heading">
        <h2 id="week-heading" className="text-sm font-semibold text-(--color-sand-700)">
          {t('common:nav.plan')}
        </h2>
        <ol className="mt-3 flex justify-between gap-1">
          {weekdayLabels(i18n.language, 'narrow').map((label, index) => {
            const day = new Date(weekStart);
            day.setDate(weekStart.getDate() + index);
            const isToday = day.toDateString() === today.toDateString();
            return (
              <li key={day.toISOString()} className="flex flex-col items-center gap-1">
                <span className="text-xs text-(--color-sand-500)">{label}</span>
                <span
                  data-numeric
                  aria-current={isToday ? 'date' : undefined}
                  className={
                    isToday
                      ? 'flex h-9 w-9 items-center justify-center rounded-full bg-(--color-amber-500) font-semibold text-(--color-sand-900)'
                      : 'flex h-9 w-9 items-center justify-center rounded-full bg-(--color-sand-100) text-(--color-sand-700)'
                  }
                >
                  {day.getDate()}
                </span>
              </li>
            );
          })}
        </ol>
      </section>

      <section className="mh-card">
        <h2 className="text-xl">{t('common:today.noWorkout')}</h2>
        <p className="mt-1 text-(--color-sand-700)">{t('common:today.noWorkoutHint')}</p>
      </section>

      {/* Provisório do Marco 0: prova que o token chega à API e volta resposta.
          Some quando a tela Hoje passar a mostrar treino de verdade. */}
      <section className="mh-card" aria-live="polite">
        <h2 className="text-sm font-semibold text-(--color-sand-700)">API</h2>
        <p className="mt-2 text-sm" data-numeric>
          {isPending ? t('common:state.loading') : (data?.status ?? t('common:state.offline'))}
        </p>
      </section>
    </div>
  );
}
